/**
 * Signing in.
 *
 * An account code, a username and a password. The code says which shop: every
 * shop has its own database and its own staff, so "admin" in one shop is a
 * different person from "admin" in the next. The till remembers the code, so
 * a cashier types it once, not every morning.
 *
 * Nothing else - no PIN, no device to register, no waiting for anyone to
 * approve the browser you happen to be standing at.
 */
import { useEffect, useState } from 'react';
import { useI18n, useT } from '../../core/i18n/index.js';
import { useAuth, OfflineSignInError, ShopSwitchBlockedError } from '../../core/state/auth.js';
import { Button, Field } from '../../core/ui/ui.js';
import { api, ApiError } from '../../core/api.js';
import { normalizeCode, thisShop } from '../../core/shop.js';
import { ShopMark } from '../../core/ui/ShopMark.js';
import { ForgotPassword } from './ForgotPassword.js';
import { LegalLinks } from '../../core/ui/bits.js';

type Branding = { storeNameEn: string; storeNameAr: string; logoDataUrl?: string | null; account: { code: string } | null };

/**
 * A till from before account codes: it has signed in here before but was never
 * told a code. Its shop is the server's default one.
 */
const REMEMBER_KEY = 'pos.rememberMe';

const usedBefore = () =>
  !!thisShop() || localStorage.getItem('pos.user') !== null || localStorage.getItem('pos.device.prefix') !== null;

export function Login() {
  const t = useT();
  const { lang, setLang } = useI18n();
  const { signInPassword } = useAuth();
  // A till that has just moved here from an old address brings its code along.
  const [code, setCode] = useState(
    () => thisShop()?.code ?? normalizeCode(new URLSearchParams(window.location.search).get('account') ?? ''),
  );
  // Whether the typed code is a shop at all ('unknown' if not). Its name is
  // not shown: nobody has signed in yet, so the page is AppBay's, not the shop's.
  const [preview, setPreview] = useState<Branding | 'unknown' | null>(null);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  // The last choice on this device, so a shop till stays ticked.
  const [remember, setRemember] = useState(() => localStorage.getItem(REMEMBER_KEY) === '1');
  const [error, setError] = useState(() => {
    // Sent here because the server ended the session - say so, once.
    if (sessionStorage.getItem('pos.sessionEnded')) {
      sessionStorage.removeItem('pos.sessionEnded');
      return t('error.sessionEnded');
    }
    return '';
  });
  const [busy, setBusy] = useState(false);
  const [forgot, setForgot] = useState(false);

  // A till from before account codes: show it the code of the shop it has
  // always used, so the cashier sees it and nothing changes for them.
  useEffect(() => {
    if (thisShop() || !usedBefore()) return;
    api<Branding>('/auth/branding', { auth: false, account: null })
      .then((b) => b.account && setCode((c) => c || b.account!.code))
      .catch(() => undefined);
  }, []);

  // Say which shop the code belongs to while it is being typed.
  useEffect(() => {
    const typed = normalizeCode(code);
    if (!typed && !usedBefore()) {
      setPreview(null);
      return;
    }
    const timer = window.setTimeout(() => {
      api<Branding>('/auth/branding', { auth: false, account: typed || null })
        .then(setPreview)
        .catch((e) => setPreview(e instanceof ApiError && !e.isOffline ? 'unknown' : null));
    }, 350);
    return () => clearTimeout(timer);
  }, [code]);

  const title = t('app.brand');

  const submit = async () => {
    setBusy(true);
    setError('');
    try {
      localStorage.setItem(REMEMBER_KEY, remember ? '1' : '0');
      await signInPassword(username.trim(), password, code, remember);
    } catch (e) {
      if (e instanceof OfflineSignInError) {
        // No connection, and the device could not vouch for this person.
        setError(
          e.reason === 'wrong' ? t('login.wrong')
            : e.reason === 'too_old' ? t('login.offlineTooOld')
            : e.reason === 'other_shop' ? t('login.offlineOtherShop')
            : t('login.offlineUnknown'),
        );
      } else if (e instanceof ShopSwitchBlockedError) {
        setError(t('login.switchBlocked', { n: e.waiting }));
      } else {
        const err = e as ApiError;
        setError(
          err.code === 'too_many' ? t('login.tooMany')
            : err.code === 'unknown_account' || err.code === 'bad_account' ? t('login.unknownAccount')
            : err.code === 'account_suspended' ? t('login.accountSuspended')
            : err.code === 'till_limit' ? t('login.tillLimit')
            : err.isOffline ? t('error.network')
            : t('login.wrong'),
        );
      }
      setPassword('');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-card">
        <div className="login-brand">
          <ShopMark name={title} logo="/icons/icon.svg" />
          <div>
            <div className="strong">{title}</div>
            <div className="xsmall muted">{t('app.tagline')}</div>
          </div>
          <div className="spacer" />
          <Button size="sm" variant="ghost" onClick={() => setLang(lang === 'ar' ? 'en' : 'ar')}>
            {lang === 'ar' ? 'EN' : 'ع'}
          </Button>
        </div>

        {forgot ? <ForgotPassword code={normalizeCode(code)} onBack={() => setForgot(false)} /> : null}
        <form
          className="col"
          hidden={forgot}
          onSubmit={(e) => {
            e.preventDefault();
            void submit();
          }}
        >
          <h2>{t('login.title')}</h2>

          <Field
            label={t('login.account')}
            hint={t('login.accountHint')}
            error={preview === 'unknown' ? t('login.unknownAccount') : undefined}
          >
            <input
              value={code}
              onChange={(e) => setCode(e.target.value)}
              autoComplete="organization"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              dir="ltr"
              autoFocus={!code}
            />
          </Field>

          <Field label={t('login.username')}>
            <input
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoComplete="username"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              dir="ltr"
              autoFocus={!!code}
            />
          </Field>

          <Field label={t('login.password')}>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              dir="ltr"
            />
          </Field>

          <label className="row" style={{ gap: 'var(--s-2)', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={remember}
              onChange={(e) => setRemember(e.target.checked)}
              style={{ width: 20, height: 20, accentColor: 'var(--accent)' }}
            />
            <span>
              {t('login.remember')}
              <span className="xsmall muted" style={{ display: 'block' }}>{t('login.rememberHint')}</span>
            </span>
          </label>

          {error ? (
            <p style={{ color: 'var(--danger)' }} role="alert">
              {error}
            </p>
          ) : null}

          <Button
            type="submit"
            variant="primary"
            size="lg"
            block
            busy={busy}
            disabled={!username.trim() || !password}
          >
            {t('login.signIn')}
          </Button>
          <button type="button" className="link-btn" onClick={() => setForgot(true)}>
            {t('forgot.link')}
          </button>
        </form>
      </div>
      <LegalLinks />
    </div>
  );
}
